'use client';

import { useMemo } from 'react';
import { Star } from 'lucide-react';
import { useMyCv } from '@/features/cv';
import {
  buildInsightCompareItems,
  type InsightCompareStatus,
} from '@/features/jobs/lib/insightCompare';
import { InsightDataDto } from '@/shared/job/insight.dto';

const copy = {
  mn: {
    title: 'Таны CV болон ажлын байрны зохицол',
    status: {
      match: 'Нийцсэн',
      advantage: 'Давуу',
      lacking: 'Дутуу',
      missing: 'Мэдээлэл дутуу',
    },
  },
  en: {
    title: 'How your CV fits this job',
    status: {
      match: 'Matched',
      advantage: 'Advantage',
      lacking: 'Lacking',
      missing: 'Missing info',
    },
  },
} as const;

const statusStyle: Record<
  InsightCompareStatus,
  { symbol: string; className: string }
> = {
  match: { symbol: '✓', className: 'text-green-500' },
  advantage: { symbol: '▲', className: 'text-green-500' },
  lacking: { symbol: '▼', className: 'text-red-500' },
  missing: { symbol: 'ⓘ', className: 'text-gray-400' },
};

export function InsightCompareCard({
  data,
  isEnglish,
}: {
  data: InsightDataDto;
  isEnglish: boolean;
}) {
  const t = isEnglish ? copy.en : copy.mn;
  const { data: cv } = useMyCv();

  const items = useMemo(
    () => (cv ? buildInsightCompareItems(data, cv, isEnglish) : []),
    [data, cv, isEnglish]
  );

  if (items.length === 0) return null;

  return (
    <div className="rounded-lg bg-primary-50 p-4">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-primary-700">
          <Star className="h-3.5 w-3.5" strokeWidth={2} />
        </span>
        <h3 className="text-sm font-semibold text-gray-900">{t.title}</h3>
      </div>
      <ul className="flex flex-col gap-2 sm:gap-y-2">
        {items.map((item) => {
          const style = statusStyle[item.status];
          return (
            <li key={item.key} className="flex items-start gap-1.5 text-sm">
              <span
                className={`mt-0.5 w-3 shrink-0 text-center text-xs ${style.className}`}
                role="img"
                aria-label={t.status[item.status]}
                title={t.status[item.status]}
              >
                {style.symbol}
              </span>
              <span className="text-gray-700">
                {item.parts.map((part, index) =>
                  part.bold ? (
                    <strong
                      key={index}
                      className="font-semibold text-gray-900"
                    >
                      {part.text}
                    </strong>
                  ) : (
                    <span key={index}>{part.text}</span>
                  )
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
